<template>
    <div v-if="contractDetails" id="accordion">
        <div class="card najiz-card-panel mb-3">
            <div class="card-header najiz-panel-header" id="history-heading">
                <a class="text-dark" data-toggle="collapse" href="#history-collapse" role="button" aria-expanded="false"
                   aria-controls="history-collapse">
                    <h4 class="mb-0 py-3">
                        <i class="fal fa-angle-down rotate-icon fa-lg ml-5"></i>
                        <b> {{ isRequest ? 'تتبع الطلب' : 'سجل الطلب' }}</b>
                    </h4>
                </a>
            </div>

            <div id="history-collapse" class="collapse" aria-labelledby="history-heading" data-parent="#accordion">
                <div class="card-body">
                    <v-timeline dense align-top>
                        <v-timeline-item small right v-for="(entry, index) in timeline.data" :key="index" :color="entry.color">
                            <h6 class="mb-1">
                                <b v-html="entry.title"></b>
                            </h6>
                            <template v-if="entry.type === 'Item'">
                                <p class="greg-hijri-time mb-0" v-for="(text, index) in entry.value" :key="index" v-html="text"></p>
                            </template>
                            <template v-if="entry.type === 'Timeline'">
                                <p class="greg-hijri-time mb-0" v-for="(text, index) in entry.subTitle" :key="'subTitle_'+index" v-html="text"></p>
                                <v-timeline dense class="mt-2 inner-timeline">
                                    <v-timeline-item small v-for="(item, index) in entry.value" :key="index" :color="item.color"
                                                     class="d-flex align-items-center pb-2">
                                        <div class="d-flex justify-content-between track-partners-background">
                                            <p class="mb-0 col-4">{{ item.name }}</p>
                                            <p :class="['mb-0 col-4 text-center', item.cssClass]">{{ item.status }}</p>
                                            <p class="mb-0 col-4">{{ item.date }}</p>
                                        </div>
                                    </v-timeline-item>
                                </v-timeline>
                            </template>
                        </v-timeline-item>
                    </v-timeline>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
    import { Component, Vue, Prop } from 'vue-property-decorator';

    import { ContractStatusEnum } from '@/enums/contract-status.enum';
    import { ContractDetails } from '@/models/contractDetailsModel';
    import { PartnerApprovalEnum } from '@/enums/partnerApprovalEnum';
    import { RequestStatusEnum } from '@/enums/request-status.enum';
    import { RequestTypeEnum } from '@/enums/request-type.enum';
    import { formatGregToHijri, formatTimeFromDate, getFormattedDate, formatDateTime } from '@/filters';
    import { HistoryTypeEnum } from '@/enums/HistoryTypeEnum';

    type EntryType = 'Item' | 'Timeline';

    type ListItem = {
        name: string;
        status: string;
        date: string;
        color: string;
        cssClass: string;
    }

    type TimelineEntry = {
        title: string;
        type: EntryType;
        color: string;
        value: string[] | ListItem[];
        subTitle: string[] | null;
    }

    /** Used to provide data to the Vuetify Timeline component */
    type TimelineProvider = {
        /** The data source. */
        data: TimelineEntry[],
        /**
         * Appends entry to the timline entries.
         * @param title The entry's title.
         * @param value The entry's value which can be an array of strings or list items.
         * @param color The entry's color.
         * @param type Used to determine the type of entry to render based on.
         */
        append: (title: string, value: string[] | ListItem[], subTitle: string[] | null, color?: string, type?: EntryType) => void
    }

    @Component
    export default class RequestTimelineComponent extends Vue {
        @Prop({ required: true }) contractDetails!: ContractDetails;

        /** Used to distinguish whether the loaded data is a request or a contract. */
        isRequest = false;

        /** The timeline provider object. */
        timeline: TimelineProvider = {
            data: [],
            append: (title: string, value: string[] | ListItem[], subTitle: string[] | null, color = 'success', type: EntryType = 'Item') => {
                this.timeline.data.push({
                    title, type, color, value, subTitle
                });
            },
        };

        mounted() {
            this.isRequest = this.$route.name === 'requestDetails';
            this.populateTimeline(this.contractDetails);
        }

        private populateTimeline(contract: ContractDetails): void {
            if (!contract?.statusHistory) return;
            contract.statusHistory.forEach((history) => {
                const isContractAttestation = history.requestTypeId === RequestTypeEnum.ContractAttestation;
                const requestTypeName = isContractAttestation ? 'توثيق عقد' : 'تسوية عقد';
                const contractCreatedDate = this.getFormatDateWithTime(contract.createdDate) ?? '';
                const createdDate = this.getFormatDateWithTime(history.createdDate) ?? '';

                const historyTypeName = history.historyTypeName ?? '';
                const historyTypeId = history.historyTypeId ?? null;
                if (history.historyTypeId === HistoryTypeEnum.UpdateBatch && history.milestoneHistory?.length) {
                    const items: ListItem[] = [];
                    history?.milestoneHistory?.forEach((batchHistory) => {
                        items.push({
                            name: `رقم الدفعة: ${batchHistory?.batchNumber}`,
                            status: `${batchHistory?.batchStatusName}`,
                            date: this.getFormatDateWithTime(batchHistory.createdDate) ?? '',
                            color: "success",
                            cssClass: "success-approval-status",
                        });
                    });
                    this.timeline.append(historyTypeName, items, null, this.getTimelineCSS(historyTypeId), 'Timeline');
                }
                else if (history.historyTypeId === HistoryTypeEnum.Updated && history.contractStatusHistoryReasons?.length) {
                    const items: ListItem[] = [];
                    history?.contractStatusHistoryReasons?.forEach((reason) => {
                        items.push({
                            name: `${reason?.reason}`,
                            status: ``,
                            date: '',
                            color: "success",
                            cssClass: "success-approval-status",
                        });
                    });
                    this.timeline.append(historyTypeName, items, [createdDate, 'الأسباب:'], this.getTimelineCSS(historyTypeId), 'Timeline');
                }
                else {
                    this.timeline.append(historyTypeName, [history.reason, createdDate], null, this.getTimelineCSS(historyTypeId));
                }

                // Append approval entries if applicable.
                //if (history.partnersApprovalHistory?.length) {
                //    const items: ListItem[] = [];
                //    history.partnersApprovalHistory.forEach((approval) => {
                //        const partner = contract.partners.find((p) => p.id === approval.najizContractPartnerId);
                //        const approvalCreatedDate = [PartnerApprovalEnum.Approved, PartnerApprovalEnum.Rejected].indexOf(approval.statusId) !== -1 ? this.formatDateTime(approval.createdDate, approval.createdDateHijri) : '';
                //        const { color, cssClass, status } = this.getApprovalUiSettings(approval.statusId, history.requestTypeId);
                //        items.push({
                //            name: `${approval?.partyName} (${partner?.partnerTarafTypeName})`,
                //            status,
                //            date: approvalCreatedDate ?? '',
                //            color,
                //            cssClass,
                //        });
                //    });
                //    this.timeline.append(`اعتماد الطلب من الأطراف ${requestTypeName}`, items, 'success', 'Timeline');
                //    const systemName = history.systemName ? history.systemName : 'خدمة التكامل  ';
                //    this.timeline.append(`تم إرسال طلب ${requestTypeName} بواسطة  : ${systemName}`, [contractCreatedDate], 'success');
                //}
            });
        }

        private getTimelineCSS(historyType: HistoryTypeEnum | null): string {
            let cssClass = 'timeline-dot-gray';
            switch (historyType) {
                case HistoryTypeEnum.IssueContract:
                case HistoryTypeEnum.Updated:
                    cssClass = 'success';
                    break;
                case HistoryTypeEnum.UpdateBatch:
                    cssClass = 'success';
                    break;
                case HistoryTypeEnum.Expired:
                    cssClass = 'timeline-dot-gray';
                    break;
                case HistoryTypeEnum.Closed:
                    cssClass = 'timeline-dot-gray';
                    break;
                case HistoryTypeEnum.Cancelled:
                    cssClass = 'timeline-dot-danger';
                    break;
            }
            return cssClass;
        }
        /**
         * Get the UI settings for an approval timeline entry.
         * @param status The partner approval status.
         * @param requestType The request type.
         */
        private getApprovalUiSettings(status: PartnerApprovalEnum, requestType: RequestTypeEnum): { color: string, cssClass: string, status: string } {
            let requestTypeName: string;

            switch (requestType) {
                case RequestTypeEnum.ContractSettlement:
                    requestTypeName = 'تسوية العقد';
                    break;
                case RequestTypeEnum.ContractAttestation:
                    requestTypeName = 'توثيق العقد';
                    break;
                default:
                    throw new Error('Invalid request type status.');
            }

            switch (status) {
                case PartnerApprovalEnum.Pending:
                    return { color: 'warning', cssClass: 'pending-approval-status', status: 'في انتظار الاعتماد' };
                case PartnerApprovalEnum.Approved:
                    return { color: 'success', cssClass: 'success-approval-status', status: `تم قبول ${requestTypeName}` };
                case PartnerApprovalEnum.Rejected:
                    return { color: 'timeline-dot-danger', cssClass: 'contract-status-rejected', status: `تم رفض ${requestTypeName}` };
                case PartnerApprovalEnum.NotApproved:
                    return { color: 'timeline-dot-gray', cssClass: 'contract-status-rejected', status: `لم يتم قبول ${requestTypeName}` };
                default:
                    throw new Error('Invalid partner approval status.');
            }
        }

        /**
         * Format date  to date string.
         * @param date The date object.
         */
        private formatDateTime(date: Date | null, dateHijri: string | null): string | null {
            if (!date) return null;

            const dateStr = date.toString();
            return `${dateHijri} هـ (${getFormattedDate(dateStr)}م) - ${formatTimeFromDate(dateStr)}`;
        }
        private getFormatDateWithTime(date: Date | null): string | null {
            if (!date) return null;
            const dateStr = date.toString();
            return formatDateTime(date, true);
        }
    }
</script>
